import React, { FC, useCallback, useContext } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import find from 'lodash/find';
import map from 'lodash/map';
import partial from 'lodash/partial';
import reduce from 'lodash/reduce';
import set from 'lodash/set';
import styled from 'styled-components';

import {
  ACTIONS_DESCRIPTION,
  EMPTY_ANSWER_ID,
  EXTERNAL_ACTION_TYPE,
  FIELDS_MAPPER,
  INTERNAL_ACTION_TYPE,
  QUICK_ACTION_NAME,
  QUICK_ACTION_TYPE,
  QUICK_ACTION_TYPES,
  QUICK_ACTION_VALUE,
  QUICK_ACTION_VALUE_PLACEHOLDER,
  QUICK_ACTIONS_UI_OPTIONS,
} from 'src/presentation/components/pages/chat/chatConstants';
import { AnswerType } from 'src/presentation/components/pages/shared/hooks/useAnswersFilter';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import { FixedWidthContentRow, ItemContainer, RowLabel } from '../ChatLayout';
import FormContext from 'src/presentation/contexts/FormContext';
import getErrorMessage from 'src/presentation/components/forms/getErrorMessage';
import { getSelectorOptions } from 'src/presentation/components/pages/shared/utils/Utils';
import Input from 'src/presentation/components/forms/Input';
import { SectionDescription } from 'src/presentation/components/forms/Layout';
import SelectAnswerDropdown from 'src/presentation/components/pages/shared/SelectAnswerDropdown';
import { usePublicAnswers } from 'src/presentation/components/pages/shared/hooks/usePublicAnswers';

type QuickActionsConfigProps = {
  configuration: any;
};

type QuickActionProps = {
  configuration: any;
  idx: number;
  inputPlaceholder?: string;
  knownAnswers: { [key: string]: string };
  label: string;
  onChange: any;
};

const QuickActionConfiguration: FC<QuickActionsConfigProps> = ({ configuration }) => {
  const { errors, resetError, updateState } = useContext(FormContext);
  const quickActionsAttr = FIELDS_MAPPER.SELF_SERVICE_QUICK_ACTIONS.path;
  const error = find(errors, error => error.attr === quickActionsAttr);
  const quickActionsValue = cloneDeep(get(configuration, quickActionsAttr));
  const configurationLanguage = get(configuration, FIELDS_MAPPER.LANGUAGE.path);

  const useOnActionChange = useCallback(
    (idx, attr, v) => {
      const value = get(v, 'target.value', v);
      const fullPathToField = `${quickActionsAttr}[${idx}].${attr}`;
      const currentFieldType = get(quickActionsValue[idx], `[${idx}].content.type`, '');
      const isFieldTypeChanged = [EXTERNAL_ACTION_TYPE, INTERNAL_ACTION_TYPE].includes(v) && currentFieldType !== v;
      let updatedQuickActions = set(quickActionsValue, `[${idx}].${attr}`, value);

      if (isFieldTypeChanged) {
        updatedQuickActions = set(updatedQuickActions, `[${idx}].content.value`, '');

        const pathId =
          EXTERNAL_ACTION_TYPE === v ? get(quickActionsValue[idx], `[${idx}].content.value`, EMPTY_ANSWER_ID) : idx;
        const pathToFiledValue = `${quickActionsAttr}[${pathId}].content.value`;
        const isError = errors.find(error => error.attr === pathToFiledValue);

        isError && resetError({ attr: pathToFiledValue, errors });
      }

      resetError({ attr: quickActionsAttr, errors });
      resetError({ attr: fullPathToField, errors });
      updateState(set({}, quickActionsAttr, updatedQuickActions));
    },
    [errors, resetError, quickActionsAttr, quickActionsValue, updateState]
  );

  const answerIds = get(configuration, quickActionsAttr, [])
    .filter(action => get(action, 'content.type') === INTERNAL_ACTION_TYPE)
    .map(action => get(action, 'content.value'));

  const [, allAnswersInLanguage] = usePublicAnswers(configurationLanguage);

  const selectedAnswers = allAnswersInLanguage.filter(answer => answerIds.includes(answer.value));
  const knownAnswers = reduce(selectedAnswers, (acc, answer = {}) => ({ ...acc, [answer.value]: answer.label }), {});

  const quickActions = map(QUICK_ACTIONS_UI_OPTIONS, (props, idx) => (
    <QuickAction
      configuration={configuration}
      idx={idx}
      key={`${quickActionsAttr}[${idx}]`}
      knownAnswers={knownAnswers}
      onChange={useOnActionChange}
      {...props}
    />
  ));

  return (
    <>
      <SubHeaderContainer>Actions</SubHeaderContainer>
      <SectionDescription>{ACTIONS_DESCRIPTION}</SectionDescription>
      <QuickActionsContainer
        content={quickActions}
        data-aid={quickActionsAttr}
        error={error ? getErrorMessage(error, '') : undefined}
      />
    </>
  );
};

const QuickAction: FC<QuickActionProps> = ({ idx, label, inputPlaceholder, configuration, knownAnswers, onChange }) => {
  const actionTypeOptions = getSelectorOptions(QUICK_ACTION_TYPES);

  const actionPrefix = `${FIELDS_MAPPER.SELF_SERVICE_QUICK_ACTIONS.path}[${idx}]`;

  const itemValue = get(configuration, `${actionPrefix}`);
  const typeValue = get(configuration, `${actionPrefix}.${QUICK_ACTION_TYPE}`);
  const valuePlaceholder = get(QUICK_ACTION_VALUE_PLACEHOLDER, typeValue, '');
  const configurationLanguage = get(configuration, FIELDS_MAPPER.LANGUAGE.path);
  const configurationAudienceId = get(configuration, FIELDS_MAPPER.AUDIENCE.path);
  const currentAnswerId = get(itemValue, 'content.value');
  const answerFieldPrefix = `${FIELDS_MAPPER.SELF_SERVICE_QUICK_ACTIONS.path}[${currentAnswerId || EMPTY_ANSWER_ID}]`;

  return (
    <ItemContainer data-aid={actionPrefix}>
      <RowLabel>{label}</RowLabel>
      <FixedWidthContentRow data-aid={`${actionPrefix}-row`}>
        <Input
          data-aid={`${actionPrefix}-${QUICK_ACTION_NAME}`}
          attr={`${actionPrefix}.${QUICK_ACTION_NAME}`}
          onChange={partial(onChange, idx, QUICK_ACTION_NAME)}
          placeholder={inputPlaceholder}
        />
        <Dropdown
          data-aid={`${actionPrefix}-content-type`}
          attr={`${actionPrefix}.${QUICK_ACTION_TYPE}`}
          onChange={partial(onChange, idx, QUICK_ACTION_TYPE)}
          options={actionTypeOptions}
        />
        {typeValue === INTERNAL_ACTION_TYPE ? (
          <SelectAnswerDropdown
            answerType={AnswerType.PUBLIC}
            attr={`${answerFieldPrefix}.content.value`}
            data-aid={`${answerFieldPrefix}-content-value`}
            showEditLink
            currentAnswerId={currentAnswerId}
            fieldName={`quickActionAnswer${idx}`}
            fitOpenerWidth={false}
            knownItems={knownAnswers}
            audienceId={configurationAudienceId}
            language={configurationLanguage}
            onAnswerIdChange={partial(onChange, idx, QUICK_ACTION_VALUE)}
          />
        ) : (
          <Input
            data-aid={`${actionPrefix}-content-value`}
            attr={`${actionPrefix}.${QUICK_ACTION_VALUE}`}
            onChange={partial(onChange, idx, QUICK_ACTION_VALUE)}
            placeholder={valuePlaceholder}
          />
        )}
      </FixedWidthContentRow>
    </ItemContainer>
  );
};

const QuickActionsContainer = ({ content, 'data-aid': dataAid, error }) => (
  <>
    <ContentWrapper error={error}>{content}</ContentWrapper>
    {error && <ErrorLabel data-aid={`${dataAid}-error`}>{error}</ErrorLabel>}
  </>
);

const ContentWrapper = styled.div``;

const ErrorLabel = styled.div`
  color: ${p => p.theme.colors.red400};
`;

export default QuickActionConfiguration;

const SubHeaderContainer = styled.div`
  margin-bottom: ${p => p.theme.spacing.insetSmall};
  font-weight: ${p => p.theme.fontWeight.heavy};
`;
